import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { useGetClientMarket, useGetClientMarketInstrument } from '@/gen/publicApi'

import { UnexpectedDataStateError } from '@/containers/unexpectedDataStateError'

import Content from '@/components/content'
import Spinner from '@/components/spinner'

import { useSetBreadcrumbs } from '@/state/breadcrumbs'

import InstrumentFundamentals from '../-components/fundamentals'
import InstrumentHistory from '../-components/history'
import InstrumentOptionMenu from '../-components/optionMenu'

import InstrumentFull from './-components/full'
import InstrumentHeading from './-components/instrumentHeading'
import InstrumentNavigation from './-components/instrumentNavigation'

export const Route = createFileRoute('/_authed/$client_code/markets/$market_code/instruments/$instrument_symbol/')({
    component: InstrumentDepthComponent,
})

function InstrumentDepthComponent() {
    const { client_code, market_code, instrument_symbol } = Route.useParams()

    const market = useGetClientMarket(client_code, market_code, { query: { suspense: true } })

    const instrument = useGetClientMarketInstrument(client_code, market_code, instrument_symbol, {
        query: { suspense: true },
    })

    // Used to show changes to the instrument immediately instead of coming from
    // the server
    const [localInstrument, setLocalInstrument] = useState(instrument.data)
    useEffect(() => {
        setLocalInstrument(instrument.data)
    }, [instrument.data])

    useSetBreadcrumbs([
        {
            to: '/$client_code/markets',
            text: 'Markets',
            params: { client_code },
        },
        'seperator',
        {
            to: '/$client_code/markets/$market_code',
            params: {
                market_code,
                client_code,
            },
            text: market.data?.name || '--',
        },
        'seperator',
        {
            to: '/$client_code/markets/$market_code/instruments',
            params: {
                market_code,
                client_code,
            },
            text: 'Instruments',
        },
        'seperator',
        {
            to: '/$client_code/markets/$market_code/instruments/$instrument_symbol',
            params: {
                market_code,
                instrument_symbol,
                client_code,
            },
            text: instrument.data?.name || '--',
        },
    ])

    if (market.isLoading || instrument.isLoading) {
        return <Spinner size="sm" absolute navVisible />
    }

    if (!market.data || !instrument.data) {
        return <UnexpectedDataStateError />
    }

    if (!localInstrument) {
        // Transient
        return <Spinner size="sm" absolute navVisible />
    }

    return (
        <>
            <InstrumentHeading market={market.data} instrument={localInstrument} />
            <Content>
                <InstrumentNavigation marketCode={market_code} instrumentSymbol={instrument_symbol} active="overview">
                    <InstrumentOptionMenu
                        clientCode={client_code}
                        instrument={localInstrument}
                        marketCode={market_code}
                        onChange={setLocalInstrument}
                    />
                </InstrumentNavigation>
                <div className="grid grid-cols-4 gap-4 mb-4">
                    <InstrumentHistory
                        clientCode={client_code}
                        marketCode={market.data.code}
                        instrument={localInstrument}
                    />
                    <InstrumentFundamentals
                        clientCode={client_code}
                        marketCode={market.data.code}
                        instrument={localInstrument}
                    />
                </div>
                <InstrumentFull clientCode={client_code} marketCode={market.data.code} instrument={localInstrument} />
            </Content>
        </>
    )
}
